import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  LogIn, Loader2, KeyRound, Mail, ArrowLeft, RefreshCw, CheckCircle,
  Eye, EyeOff, ShieldCheck, Sparkles, Layers, Lock,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import BrandLogo from '../components/BrandLogo';
import axios from 'axios';
import { APP_NAME, APP_TAGLINE, APP_PLATFORM_LABEL } from '../config/appConfig';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REMEMBER_KEY = 'clarity_login_email';

// Three feature highlights for the branding panel. Kept out of JSX so we can
// map cleanly + tweak copy in one place.
const FEATURES = [
  {
    icon: ShieldCheck,
    title: 'Multi-Level Approvals',
    desc: 'Structured editorial gates for enterprise content standards.',
  },
  {
    icon: Sparkles,
    title: 'AI-Assisted Content Workflow',
    desc: 'Automated brief generation and competitive gap analysis.',
  },
  {
    icon: Layers,
    title: 'RBAC & Audit Trail',
    desc: 'Granular permissions and full change logging for compliance.',
  },
];

const BrandingPanel = () => (
  <div
    className="relative hidden lg:flex flex-col justify-between overflow-hidden text-white p-12 xl:p-16"
    style={{
      background:
        'radial-gradient(circle at 20% 10%, rgba(198,252,61,0.14) 0%, transparent 45%),' +
        'radial-gradient(circle at 80% 80%, rgba(51,138,241,0.22) 0%, transparent 50%),' +
        'linear-gradient(135deg, #0a1628 0%, #10233d 55%, #0a1628 100%)',
    }}
  >
    {/* Subtle dot grid texture */}
    <div
      className="absolute inset-0 opacity-[0.08] pointer-events-none"
      style={{
        backgroundImage:
          'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.6) 1px, transparent 0)',
        backgroundSize: '28px 28px',
      }}
      aria-hidden="true"
    />
    {/* Soft glow accents */}
    <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-brand-lime/10 blur-3xl" aria-hidden="true" />
    <div className="absolute -bottom-32 -right-32 w-[28rem] h-[28rem] rounded-full bg-brand-accent/15 blur-3xl" aria-hidden="true" />

    {/* Logo */}
    <div className="relative z-10 flex items-center gap-3">
      <BrandLogo size={44} className="rounded-xl shadow-lg shadow-brand-accent/30" />
      <div className="leading-tight">
        <div className="text-xl font-bold tracking-tight">{APP_NAME}</div>
        <div className="text-xs text-slate-400 mt-0.5">Operations • Multi-Brand</div>
      </div>
    </div>

    {/* Headline + supporting copy */}
    <div className="relative z-10 max-w-xl">
      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-white/5 border border-white/10 text-slate-300 mb-6">
        {APP_PLATFORM_LABEL}
      </span>
      <h1 className="text-4xl xl:text-5xl font-bold leading-[1.1] tracking-tight mb-5">
        Where SEO operations,<br />
        approvals, content, and<br />
        teams work together.
      </h1>
      <p className="text-base text-slate-300/90 max-w-md leading-relaxed">
        Streamline your search engine presence with high-performance workflows and AI-integrated operations.
      </p>
    </div>

    {/* Feature cards */}
    <div className="relative z-10 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl">
      {FEATURES.map(({ icon: Icon, title, desc }) => (
        <div
          key={title}
          className="group rounded-xl p-5 bg-white/5 border border-white/10 backdrop-blur-sm hover:bg-white/[0.08] hover:border-brand-accent/40 hover:-translate-y-0.5 transition-all duration-200"
        >
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-brand-accent/25 to-brand-lime/20 border border-brand-accent/30 flex items-center justify-center mb-3 group-hover:scale-105 transition-transform">
            <Icon size={18} className="text-brand-accent" />
          </div>
          <div className="text-sm font-semibold text-white mb-1">{title}</div>
          <div className="text-xs text-slate-400 leading-relaxed">{desc}</div>
        </div>
      ))}
    </div>
  </div>
);

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [loading, setLoading] = useState(false);
  const emailRef = useRef(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Forgot Password state (existing flow preserved)
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotStep, setForgotStep] = useState('email');
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetOtp, setResetOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Remember-me: prefill email + autofocus on mount.
  useEffect(() => {
    try {
      const remembered = localStorage.getItem(REMEMBER_KEY);
      if (remembered) {
        setEmail(remembered);
        setRememberMe(true);
      }
    } catch (_) { /* localStorage may be unavailable */ }
    setTimeout(() => emailRef.current?.focus(), 0);
  }, []);

  const validateEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    setEmailError('');
    setLoading(true);

    try {
      const user = await login(email, password);
      if (rememberMe) {
        try { localStorage.setItem(REMEMBER_KEY, email); } catch (_) {}
      } else {
        try { localStorage.removeItem(REMEMBER_KEY); } catch (_) {}
      }
      toast.success(`Welcome back, ${user.full_name}!`);

      // Dedicated "Social Media" RBAC role always lands straight on the
      // Social Media module, regardless of its (shared) legacy role_level.
      if (user.rbac_role_name === 'social_media') {
        navigate('/social-media');
        return;
      }

      const roleRedirects = {
        'Manager': '/dashboard',
        'Admin': '/dashboard',
        'Team Leader': '/dashboard',
        'Team Member': '/my-day',
        'Developer': '/my-day',
        'UI/UX Designer': '/my-day',
        'Creative Head': '/social-media',
        'Read-only': '/kanban',
      };
      navigate(roleRedirects[user.role_level] || '/dashboard');
    } catch (error) {
      let errorMessage = 'Login failed';
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          const first = error.response.data.detail[0];
          if (first?.msg?.includes('special-use or reserved name')) {
            errorMessage = 'Please use a valid email domain (e.g., @gmail.com, @company.com). Note: .local domains are not supported.';
          } else {
            errorMessage = first?.msg || 'Login failed';
          }
        } else if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        }
      }
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // ── Forgot Password handlers ──────────────────────────────────────────────
  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      toast.success('Password reset OTP sent! Check your email.');
      setForgotStep('otp');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPasswordSubmit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
    if (newPassword.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        email: forgotEmail, otp: resetOtp,
        new_password: newPassword, confirm_password: confirmPassword,
      });
      toast.success('Password reset successfully!');
      setForgotStep('success');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/auth/resend-reset-otp`, { email: forgotEmail });
      toast.success('New OTP sent to your email');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  const resetForgotForm = () => {
    setShowForgotPassword(false);
    setForgotStep('email');
    setForgotEmail('');
    setResetOtp('');
    setNewPassword('');
    setConfirmPassword('');
  };

  // ── Forgot Password screens ───────────────────────────────────────────────
  if (showForgotPassword) {
    return (
      <div className="min-h-screen grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] bg-slate-50">
        <BrandingPanel />
        <div className="flex items-center justify-center p-6 sm:p-10 bg-white">
          <div className="w-full max-w-md">
            <div className="text-center mb-8">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-amber-500/20">
                <KeyRound className="text-white" size={26} />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-1.5">
                {forgotStep === 'success' ? 'Password Reset!' : 'Reset Password'}
              </h2>
              <p className="text-slate-500 text-sm">
                {forgotStep === 'email' && 'Enter your email to receive a reset OTP'}
                {forgotStep === 'otp' && 'Enter the OTP and your new password'}
                {forgotStep === 'success' && 'Your password has been changed successfully'}
              </p>
            </div>

            {forgotStep === 'email' && (
              <form onSubmit={handleForgotPasswordSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="forgot-email">Email Address</Label>
                  <div className="relative">
                    <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                    <Input
                      id="forgot-email"
                      type="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      placeholder="you@company.com"
                      required
                      data-testid="forgot-email-input"
                      className="h-11 pl-9"
                    />
                  </div>
                </div>

                <Button type="submit" disabled={loading} data-testid="forgot-submit-button"
                  className="w-full h-11 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold">
                  {loading ? <><Loader2 className="animate-spin mr-2" size={18} /> Sending OTP...</> : <><Mail className="mr-2" size={18} /> Send Reset OTP</>}
                </Button>

                <Button type="button" variant="ghost" onClick={resetForgotForm} className="w-full" data-testid="back-to-login-button">
                  <ArrowLeft className="mr-2" size={18} /> Back to Login
                </Button>
              </form>
            )}

            {forgotStep === 'otp' && (
              <form onSubmit={handleResetPasswordSubmit} className="space-y-5">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center">
                  <Mail className="mx-auto text-amber-500 mb-2" size={28} />
                  <p className="text-sm text-amber-800">OTP sent to <strong>{forgotEmail}</strong></p>
                  <p className="text-xs text-amber-600 mt-1">Expires in 10 minutes</p>
                  <p className="text-xs text-amber-700 mt-2 font-medium">Check your spam/junk folder if not received</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="reset-otp">Enter OTP</Label>
                  <Input id="reset-otp" type="text" value={resetOtp}
                    onChange={(e) => setResetOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000" maxLength={6} required data-testid="reset-otp-input"
                    className="h-11 text-center text-2xl tracking-widest font-mono" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="new-password">New Password</Label>
                  <div className="relative">
                    <Input id="new-password" type={showNewPassword ? 'text' : 'password'}
                      value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password" required minLength={8}
                      data-testid="new-password-input" className="h-11 pr-10" />
                    <button type="button" onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                      {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm New Password</Label>
                  <div className="relative">
                    <Input id="confirm-password" type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm new password" required minLength={8}
                      data-testid="confirm-password-input" className="h-11 pr-10" />
                    <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                      {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  {newPassword && confirmPassword && newPassword !== confirmPassword && (
                    <p className="text-xs text-red-500">Passwords do not match</p>
                  )}
                </div>

                <div className="flex justify-center">
                  <button type="button" onClick={handleResendOtp} disabled={loading}
                    className="text-sm text-amber-600 hover:text-amber-700 flex items-center gap-1">
                    <RefreshCw size={14} /> Resend OTP
                  </button>
                </div>

                <Button type="submit" disabled={loading || resetOtp.length !== 6 || newPassword !== confirmPassword}
                  data-testid="reset-password-button"
                  className="w-full h-11 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold">
                  {loading ? <><Loader2 className="animate-spin mr-2" size={18} /> Resetting...</> : <><KeyRound className="mr-2" size={18} /> Reset Password</>}
                </Button>

                <Button type="button" variant="ghost" onClick={resetForgotForm} className="w-full">
                  <ArrowLeft className="mr-2" size={18} /> Cancel
                </Button>
              </form>
            )}

            {forgotStep === 'success' && (
              <div className="space-y-6">
                <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                  <CheckCircle className="mx-auto text-green-500 mb-3" size={48} />
                  <p className="text-green-800 font-medium">Password Reset Successful!</p>
                  <p className="text-sm text-green-600 mt-2">You can now login with your new password.</p>
                </div>
                <Button type="button" onClick={resetForgotForm} data-testid="login-after-reset-button"
                  className="w-full h-11 bg-brand-navy hover:bg-brand-navy-hover text-white font-semibold">
                  <LogIn className="mr-2" size={18} /> Go to Login
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Primary login screen ──────────────────────────────────────────────────
  return (
    <div className="min-h-screen grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] bg-white">
      <BrandingPanel />

      <div className="flex items-center justify-center p-6 sm:p-10 bg-white">
        <div className="w-full max-w-md">
          {/* Compact brand mark — visible only when branding panel is hidden (mobile/tablet) */}
          <div className="lg:hidden flex items-center gap-2.5 mb-8">
            <BrandLogo size={40} className="rounded-xl shadow-md" />
            <div className="leading-tight">
              <div className="text-base font-bold text-slate-900">{APP_NAME}</div>
              <div className="text-[11px] text-slate-500">Operations • Multi-Brand</div>
            </div>
          </div>

          <div className="mb-8">
            <h2 className="text-3xl font-bold text-slate-900 tracking-tight mb-2">Welcome back</h2>
            <p className="text-sm text-slate-500">Sign in to your enterprise account to continue.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form" noValidate>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-slate-700">Email address</Label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                <Input
                  id="email"
                  ref={emailRef}
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); if (emailError) setEmailError(''); }}
                  placeholder="you@company.com"
                  autoComplete="email"
                  required
                  data-testid="login-email-input"
                  aria-invalid={!!emailError}
                  className={`h-11 pl-9 transition-colors ${emailError ? 'border-red-500 focus-visible:ring-red-500' : 'focus-visible:ring-brand-accent'}`}
                />
              </div>
              {emailError && (
                <p data-testid="email-error" className="text-xs text-red-600 font-medium">{emailError}</p>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-sm font-medium text-slate-700">Password</Label>
                <button type="button" onClick={() => setShowForgotPassword(true)}
                  className="text-xs text-brand-accent hover:text-brand-accent-hover font-medium"
                  data-testid="forgot-password-link">
                  Forgot password?
                </button>
              </div>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  required
                  data-testid="login-password-input"
                  className="h-11 pl-9 pr-10 focus-visible:ring-brand-accent transition-colors"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  data-testid="toggle-password-visibility">
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="flex items-center gap-2 pt-1">
              <Checkbox
                id="remember-me"
                checked={rememberMe}
                onCheckedChange={(checked) => setRememberMe(!!checked)}
                data-testid="remember-me-checkbox"
              />
              <Label htmlFor="remember-me" className="text-sm text-slate-600 font-normal cursor-pointer select-none">
                Remember me on this device
              </Label>
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full h-11 bg-brand-navy hover:bg-brand-navy-hover text-white font-semibold shadow-md shadow-brand-navy/20 hover:shadow-brand-accent/30 transition-all active:scale-[0.99]"
            >
              {loading ? (
                <><Loader2 className="animate-spin mr-2" size={18} /> Signing in...</>
              ) : (
                <><LogIn className="mr-2" size={18} /> Sign in</>
              )}
            </Button>
          </form>

          <p className="text-[11px] text-slate-400 text-center mt-8 leading-relaxed">
            By signing in you agree to our{' '}
            <span className="text-slate-500 hover:text-slate-700 cursor-pointer">Terms</span> and{' '}
            <span className="text-slate-500 hover:text-slate-700 cursor-pointer">Privacy Policy</span>.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
